import React, { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import html2pdf from 'html2pdf.js';
import {
  User, Shield, FileText, Star, DollarSign, Calendar, MessageSquare,
  Phone, Mail, MapPin, CreditCard, HeartPulse, Clock, Trash2, Plus,
  Check, AlertCircle, Edit2, Upload, ExternalLink, ThumbsUp, Activity,
  Percent, Download, Search, FileSpreadsheet
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { formatDateFR } from '../../shared/utils/helpers';
import { uploadToStorage } from '../../../config/supabase';
import { computeCommissionStats, withPaidStatus } from '../utils/commissionService';
import Modal from '../../shared/components/Modal';
import DocumentViewerModal from '../../shared/components/DocumentViewerModal';

export default function StaffDetailModal({ employeeId, onClose, settings }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  
  const { data: staffList, update: updateStaff } = useCollection('staff');
  const { data: allPayments, add: addPayment, remove: removePayment } = useCollection('staff_payments');
  const { data: allCommissions } = useCollection('staff_commissions');

  const employee = useMemo(() => {
    return staffList.find(s => s.id === employeeId);
  }, [staffList, employeeId]);

  const [activeTab, setActiveTab] = useState('overview');
  const [submitting, setSubmitting] = useState(false);
  const [viewDoc, setViewDoc] = useState(null); // private doc opened via signed-URL viewer

  // --- Document State ---
  const [docName, setDocName] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [docExpiry, setDocExpiry] = useState('');

  // --- Attendance State ---
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceStatus, setAttendanceStatus] = useState('present'); // present, absent, late, leave

  // --- Review State ---
  const [reviewComment, setReviewComment] = useState('');
  const [reviewRating, setReviewRating] = useState(5);

  // --- Note State ---
  const [noteText, setNoteText] = useState('');

  // --- Commission State ---
  const [commSearch, setCommSearch] = useState('');
  const [commStatus, setCommStatus] = useState('all'); // all | pending | paid
  const [commSort, setCommSort] = useState('date_desc');
  const [payAmount, setPayAmount] = useState('');
  const [payRef, setPayRef] = useState('');
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);
  const [payNote, setPayNote] = useState('');

  if (!employee) return null;

  // Payments for this employee
  const payments = useMemo(() => {
    const mine = allPayments.filter(p => p.employeeId === employee.id);
    return [...mine].sort((a, b) => (b.paymentDate || '').localeCompare(a.paymentDate || ''));
  }, [allPayments, employee.id]);

  // Compute Net Paid
  const financeStats = useMemo(() => {
    const totalSalary = payments.filter(p => p.type === 'salary').reduce((s, p) => s + (p.amount || 0), 0);
    const totalAdvance = payments.filter(p => p.type === 'advance').reduce((s, p) => s + (p.amount || 0), 0);
    const totalBonus = payments.filter(p => p.type === 'bonus').reduce((s, p) => s + (p.amount || 0), 0);
    const totalPenalty = payments.filter(p => p.type === 'penalty').reduce((s, p) => s + (p.amount || 0), 0);
    const netTotal = totalSalary + totalBonus - totalPenalty;
    return { totalSalary, totalAdvance, totalBonus, totalPenalty, netTotal };
  }, [payments]);

  // ── Commissions ────────────────────────────────────────────────────────────
  const myCommissions = useMemo(
    () => (allCommissions || []).filter(c => c.staffId === employee.id),
    [allCommissions, employee.id]
  );
  const commissionStats = useMemo(
    () => computeCommissionStats(myCommissions, payments),
    [myCommissions, payments]
  );
  const commissionRows = useMemo(() => {
    let rows = withPaidStatus(myCommissions, commissionStats.totalPaid);
    if (commStatus !== 'all') rows = rows.filter(r => r.status === commStatus);
    const q = commSearch.trim().toLowerCase();
    if (q) rows = rows.filter(r =>
      (r.contractNumber || '').toLowerCase().includes(q) ||
      (r.customer || '').toLowerCase().includes(q) ||
      (r.vehicle || '').toLowerCase().includes(q));
    rows.sort((a, b) => {
      switch (commSort) {
        case 'date_asc': return (a.date || '').localeCompare(b.date || '');
        case 'amount_desc': return (Number(b.amount) || 0) - (Number(a.amount) || 0);
        case 'amount_asc': return (Number(a.amount) || 0) - (Number(b.amount) || 0);
        default: return (b.date || '').localeCompare(a.date || '');
      }
    });
    return rows;
  }, [myCommissions, commissionStats.totalPaid, commStatus, commSearch, commSort]);

  const exportCommissionsExcel = () => {
    try {
      const data = commissionRows.map(r => ({
        Date: formatDateFR(r.date),
        Contrat: r.contractNumber || '',
        Client: r.customer || '',
        Vehicule: r.vehicle || '',
        Commission: Number(r.amount) || 0,
        Statut: r.status === 'paid' ? (isAr ? 'مدفوع' : 'Payé') : (isAr ? 'قيد الانتظار' : 'En attente'),
        Notes: r.notes || '',
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Commissions');
      XLSX.writeFile(wb, `commissions_${(employee.name || 'staff').replace(/\s+/g, '_')}.xlsx`);
    } catch (e) { alert('❌ ' + e.message); }
  };

  const exportCommissionsPdf = () => {
    try {
      const cur = settings.currency || 'MAD';
      const body = commissionRows.map(r => `
        <tr>
          <td>${formatDateFR(r.date)}</td>
          <td>${r.contractNumber || ''}</td>
          <td>${(r.customer || '').replace(/</g, '&lt;')}</td>
          <td>${(r.vehicle || '').replace(/</g, '&lt;')}</td>
          <td style="text-align:right">${Number(r.amount) || 0} ${cur}</td>
          <td>${r.status === 'paid' ? (isAr ? 'مدفوع' : 'Payé') : (isAr ? 'قيد الانتظار' : 'En attente')}</td>
        </tr>`).join('');
      const el = document.createElement('div');
      el.innerHTML = `
        <div style="font-family:Arial;padding:16px">
          <h2 style="margin:0 0 6px">${isAr ? 'سجل العمولات' : 'Historique des commissions'} — ${employee.name || ''}</h2>
          <p style="font-size:12px;color:#555">${isAr ? 'إجمالي' : 'Total'}: ${commissionStats.totalEarned} ${cur} ·
            ${isAr ? 'مدفوع' : 'Payé'}: ${commissionStats.totalPaid} ${cur} ·
            ${isAr ? 'المتبقي' : 'Restant'}: ${commissionStats.remaining} ${cur}</p>
          <table style="width:100%;border-collapse:collapse;font-size:12px" border="1" cellpadding="6">
            <thead><tr style="background:#f3f4f6">
              <th>${isAr ? 'التاريخ' : 'Date'}</th><th>${isAr ? 'رقم العقد' : 'N° Contrat'}</th>
              <th>${isAr ? 'الزبون' : 'Client'}</th><th>${isAr ? 'السيارة' : 'Véhicule'}</th>
              <th>${isAr ? 'العمولة' : 'Commission'}</th><th>${isAr ? 'الحالة' : 'Statut'}</th>
            </tr></thead><tbody>${body}</tbody>
          </table>
        </div>`;
      html2pdf().set({
        margin: 10,
        filename: `commissions_${(employee.name || 'staff').replace(/\s+/g, '_')}.pdf`,
        html2canvas: { scale: 2 }, jsPDF: { unit: 'mm', format: 'a4' },
      }).from(el).save();
    } catch (e) { alert('❌ ' + e.message); }
  };

  const payCommission = async () => {
    const amt = parseFloat(payAmount);
    if (!Number.isFinite(amt) || amt <= 0) return alert(isAr ? 'مبلغ غير صحيح' : 'Montant invalide');
    if (amt > commissionStats.remaining + 0.001 &&
        !window.confirm(isAr ? 'المبلغ يتجاوز الرصيد المتبقي. متابعة؟' : 'Le montant dépasse le solde restant. Continuer ?')) return;
    setSubmitting(true);
    try {
      await addPayment({
        employeeId: employee.id,
        employeeName: employee.name,
        type: 'commission',
        amount: amt,
        paymentDate: payDate,
        reference: payRef.trim(),
        note: payNote.trim(),
        createdAt: new Date().toISOString(),
      });
      setPayAmount(''); setPayRef(''); setPayNote('');
      alert('✅ ' + (isAr ? 'تم تسجيل دفع العمولة' : 'Paiement de commission enregistré'));
    } catch (e) { alert('❌ ' + e.message); }
    finally { setSubmitting(false); }
  };

  // Attendance summary
  const attendanceRecords = employee.attendance || {};
  const attendanceStats = useMemo(() => {
    const records = Object.values(attendanceRecords);
    if (records.length === 0) return { rate: 100, present: 0, late: 0, absent: 0, leave: 0 };
    const present = records.filter(r => r === 'present').length;
    const late = records.filter(r => r === 'late').length;
    const absent = records.filter(r => r === 'absent').length;
    const leave = records.filter(r => r === 'leave').length;
    
    // Late counts as present for rate but shown separately
    const rate = Math.round(((present + late) / records.length) * 100);
    return { rate, present, late, absent, leave, total: records.length };
  }, [attendanceRecords]);

  // ----------------------------------------------------
  // Handlers
  // ----------------------------------------------------

  const handleTogglePermission = async (permKey) => {
    const currentPerms = employee.permissions || {};
    const updatedPerms = { ...currentPerms, [permKey]: !currentPerms[permKey] };
    try {
      await updateStaff(employee.id, { permissions: updatedPerms });
    } catch (err) {
      alert('Error updating permissions: ' + err.message);
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!docName.trim() || !docFile) {
      return alert(isAr ? '⚠️ الرجاء إدخال اسم المستند والملف' : '⚠️ Nom du document et fichier requis');
    }
    
    setSubmitting(true);
    try {
      const fileUrl = await uploadToStorage(docFile, 'staff_docs');
      const newDoc = {
        name: docName.trim(),
        url: fileUrl,
        expiryDate: docExpiry || null,
        uploadedAt: new Date().toISOString()
      };
      
      const existingDocs = employee.documents || [];
      await updateStaff(employee.id, { documents: [...existingDocs, newDoc] });
      
      setDocName('');
      setDocExpiry('');
      setDocFile(null);
      // Clear file input
      const fileInput = document.getElementById('staff-doc-file');
      if (fileInput) fileInput.value = '';
      
      alert(isAr ? '✅ تم حفظ المستند' : '✅ Document enregistré');
    } catch (err) {
      alert('Error uploading document: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDocument = async (index) => {
    if (!confirm(isAr ? 'حذف هذا المستند؟' : 'Supprimer ce document?')) return;
    try {
      const existingDocs = [...(employee.documents || [])];
      existingDocs.splice(index, 1);
      await updateStaff(employee.id, { documents: existingDocs });
    } catch (err) {
      alert('Error deleting document: ' + err.message);
    }
  };

  const handleLogAttendance = async (e) => {
    e.preventDefault();
    if (!attendanceDate) return;
    
    try {
      const existingAttendance = { ...attendanceRecords };
      existingAttendance[attendanceDate] = attendanceStatus;
      await updateStaff(employee.id, { attendance: existingAttendance });
      alert(isAr ? '✅ تم تسجيل الحضور' : '✅ Présence enregistrée');
    } catch (err) {
      alert('Error saving attendance: ' + err.message);
    }
  };

  const handleUpdateSchedule = async (day, value) => {
    const currentSchedule = employee.schedule || {};
    const updatedSchedule = { ...currentSchedule, [day]: value };
    try {
      await updateStaff(employee.id, { schedule: updatedSchedule });
    } catch (err) {
      alert('Error updating schedule: ' + err.message);
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!reviewComment.trim()) return;
    
    try {
      const newReview = {
        rating: reviewRating,
        comment: reviewComment.trim(),
        date: new Date().toISOString().split('T')[0],
        reviewer: 'Admin'
      };
      const existingReviews = employee.reviews || [];
      await updateStaff(employee.id, { reviews: [newReview, ...existingReviews] });
      setReviewComment('');
      setReviewRating(5);
    } catch (err) {
      alert('Error adding review: ' + err.message);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    
    try {
      const newNote = {
        text: noteText.trim(),
        date: new Date().toISOString()
      };
      const existingNotes = employee.notesList || [];
      await updateStaff(employee.id, { notesList: [newNote, ...existingNotes] });
      setNoteText('');
    } catch (err) {
      alert('Error adding note: ' + err.message);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!confirm(isAr ? 'حذف هذه الدفعة؟' : 'Supprimer ce paiement?')) return;
    try {
      await removePayment(paymentId);
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // --- Translation helpers ---
  const t = (fr, ar) => (isAr ? ar : fr);

  // Tabs definitions
  const tabs = [
    { id: 'overview', label: t('Profil', 'الملف الشخصي'), icon: User },
    { id: 'planning', label: t('Planning', 'الجدول والحضور'), icon: Calendar },
    { id: 'documents', label: t('Documents', 'الوثائق'), icon: FileText },
    { id: 'permissions', label: t('Permissions', 'الصلاحيات'), icon: Shield },
    { id: 'performance', label: t('Performance', 'الأداء'), icon: Star },
    { id: 'notes', label: t('Notes', 'ملاحظات'), icon: MessageSquare },
    { id: 'finances', label: t('Finances', 'المالية'), icon: DollarSign },
    { id: 'commission', label: t('Commissions', 'العمولات'), icon: Percent }
  ];

  return (
    <Modal onClose={onClose} title={t(`Profil de ${employee.name}`, `ملف الموظف ${employee.name}`)}>
      <div className="flex flex-col lg:flex-row gap-6 min-h-[500px]" dir={isAr ? 'rtl' : 'ltr'}>
        
        {/* Sidebar Nav */}
        <div className="lg:w-1/4 shrink-0 flex flex-row lg:flex-col overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 border-b lg:border-b-0 lg:border-r border-gray-200 gap-1 scrollbar-none">
          
          {/* Circular avatar banner on top of side menu for desktop */}
          <div className="hidden lg:flex flex-col items-center p-4 mb-4 bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl border border-gray-200">
            {employee.photo ? (
              <img 
                src={employee.photo} 
                alt={employee.name} 
                className="w-20 h-20 rounded-full object-cover ring-4 ring-blue-500 shadow-md mb-2" 
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-blue-600 text-white font-black flex items-center justify-center text-3xl shadow-md mb-2">
                {employee.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="font-bold text-gray-900 text-center truncate w-full">{employee.name}</div>
            <div className="text-xs text-blue-600 font-bold px-2 py-0.5 bg-blue-50 rounded-full border border-blue-100 mt-1">
              {employee.role || '-'}
            </div>
          </div>

          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-xl whitespace-nowrap transition-all ${
                  isActive 
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-200' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Body */}
        <div className="flex-1 lg:pl-2">
          
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              
              {/* Profile Card Banner on mobile */}
              <div className="flex lg:hidden items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl mb-4">
                {employee.photo ? (
                  <img 
                    src={employee.photo} 
                    alt={employee.name} 
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-blue-500 shadow" 
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-blue-600 text-white font-black flex items-center justify-center text-2xl shadow">
                    {employee.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-gray-900 text-base">{employee.name}</h3>
                  <span className="inline-block text-[10px] text-blue-700 font-bold px-2 py-0.5 bg-blue-50 rounded-full border border-blue-100 mt-1">
                    {employee.role}
                  </span>
                </div>
              </div>

              {/* General Contact Information Grid */}
              <div>
                <h3 className="text-sm font-black text-gray-900 border-b pb-2 mb-3 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-blue-600" />
                  {t('Informations Générales', 'المعلومات العامة')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <Phone className="w-5 h-5 text-gray-400 shrink-0" />
                    <div>
                      <div className="text-[10px] text-gray-400 font-bold uppercase">{t('Téléphone', 'الهاتف')}</div>
                      <div className="text-sm font-semibold text-gray-800 font-mono">{employee.phone || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <Mail className="w-5 h-5 text-gray-400 shrink-0" />
                    <div>
                      <div className="text-[10px] text-gray-400 font-bold uppercase">{t('Email', 'البريد الإلكتروني')}</div>
                      <div className="text-sm font-semibold text-gray-800 font-mono">{employee.email || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <AlertCircle className="w-5 h-5 text-gray-400 shrink-0" />
                    <div>
                      <div className="text-[10px] text-gray-400 font-bold uppercase">{t('CIN', 'رقم البطاقة الوطنية')}</div>
                      <div className="text-sm font-semibold text-gray-800 font-mono">{employee.cin || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <Calendar className="w-5 h-5 text-gray-400 shrink-0" />
                    <div>
                      <div className="text-[10px] text-gray-400 font-bold uppercase">{t('Date embauche', 'تاريخ التوظيف')}</div>
                      <div className="text-sm font-semibold text-gray-800">
                        {employee.hireDate ? formatDateFR(employee.hireDate) : '-'}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 mt-3">
                  <MapPin className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] text-gray-400 font-bold uppercase">{t('Adresse', 'العنوان')}</div>
                    <div className="text-sm font-semibold text-gray-800">{employee.address || '-'}</div>
                  </div>
                </div>
              </div>

              {/* Finance details */}
              <div>
                <h3 className="text-sm font-black text-gray-900 border-b pb-2 mb-3 flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4 text-emerald-600" />
                  {t('Salaire & Banque', 'الراتب والحساب البنكي')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-3 bg-emerald-50/50 border border-emerald-100 rounded-xl">
                    <div className="text-[10px] text-emerald-800 font-bold uppercase">{t('Contrat de Salaire', 'عقد العمل والراتب')}</div>
                    <div className="text-lg font-black text-emerald-700 mt-1">
                      {employee.salaryAmount} {settings.currency || 'MAD'}
                      <span className="text-xs font-medium text-emerald-600">
                        {employee.salaryType === 'monthly' ? t(' /mois', ' /شهريا') : t(' /jour', ' /يوميا')}
                      </span>
                    </div>
                    {employee.salaryType === 'monthly' && (
                      <div className="text-[10px] text-emerald-600 mt-1 font-semibold">
                        📅 {t(`Payé le ${employee.paymentDay} du mois`, `تاريخ الصرف: ${employee.paymentDay} في الشهر`)}
                      </div>
                    )}
                  </div>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                    <div className="text-[10px] text-gray-400 font-bold uppercase">{t('RIB de Virement', 'الحساب البنكي (RIB)')}</div>
                    <div className="text-xs font-mono font-semibold text-gray-800 mt-2 break-all">
                      {employee.bankAccount || t('Non configuré', 'غير محدد')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="text-sm font-black text-gray-900 border-b pb-2 mb-3 flex items-center gap-1.5">
                  <HeartPulse className="w-4 h-4 text-red-500" />
                  {t('Contact d\'urgence', 'جهة الاتصال في حالة الطوارئ')}
                </h3>
                <div className="p-4 bg-red-50/30 border border-red-100 rounded-xl">
                  {employee.emergencyContact ? (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-lg shrink-0">
                        🆘
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900">{employee.emergencyContact}</div>
                        <div className="text-xs text-gray-500">{t('Personne de confiance à contacter en cas de besoin.', 'الشخص الذي يمكن الاتصال به عند الضرورة.')}</div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">{t('Aucun contact d\'urgence renseigné.', 'لم يتم تحديد جهة اتصال للطوارئ.')}</p>
                  )}
                </div>
              </div>

            </div>
          )}

          {/* TAB 2: PLANNING & ATTENDANCE */}
          {activeTab === 'planning' && (
            <div className="space-y-6">
              
              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-center">
                  <div className="text-[10px] font-bold text-blue-700 uppercase mb-1">{t('Taux de Présence', 'نسبة الحضور')}</div>
                  <div className="text-2xl font-black text-blue-900">{attendanceStats.rate}%</div>
                </div>
                <div className="p-3 bg-green-50 border border-green-100 rounded-xl text-center">
                  <div className="text-[10px] font-bold text-green-700 uppercase mb-1">{t('Présent', 'حاضر')}</div>
                  <div className="text-2xl font-black text-green-900">{attendanceStats.present}</div>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-center">
                  <div className="text-[10px] font-bold text-amber-700 uppercase mb-1">{t('Retards', 'متأخر')}</div>
                  <div className="text-2xl font-black text-amber-900">{attendanceStats.late}</div>
                </div>
                <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-center">
                  <div className="text-[10px] font-bold text-red-700 uppercase mb-1">{t('Absences', 'غائب')}</div>
                  <div className="text-2xl font-black text-red-900">{attendanceStats.absent}</div>
                </div>
              </div>

              {/* Log Attendance Form */}
              <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                <h3 className="text-xs font-bold text-gray-700 uppercase mb-3">{t('Enregistrer une présence', 'تسجيل حضور / غياب')}</h3>
                <form onSubmit={handleLogAttendance} className="flex flex-wrap gap-3 items-end">
                  <div className="w-full sm:w-auto">
                    <label className="block text-xs font-bold text-gray-500 mb-1">{t('Date', 'التاريخ')}</label>
                    <input 
                      type="date" 
                      value={attendanceDate}
                      onChange={e => setAttendanceDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none bg-white focus:ring-2 focus:ring-blue-500" 
                    />
                  </div>
                  <div className="flex-1 min-w-[150px]">
                    <label className="block text-xs font-bold text-gray-500 mb-1">{t('Statut', 'الحالة')}</label>
                    <select 
                      value={attendanceStatus}
                      onChange={e => setAttendanceStatus(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none bg-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="present">🟢 {t('Présent', 'حاضر')}</option>
                      <option value="late">🟡 {t('Retard', 'متأخر')}</option>
                      <option value="absent">🔴 {t('Absent', 'غائب')}</option>
                      <option value="leave">🔵 {t('En Congé', 'في عطلة')}</option>
                    </select>
                  </div>
                  <button 
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg transition"
                  >
                    {t('Enregistrer', 'حفظ')}
                  </button>
                </form>
              </div>

              {/* Weekly Shifts / Schedules */}
              <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                <h3 className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-1">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>{t('Planning hebdomadaire / Horaires', 'أوقات العمل الأسبوعية')}</span>
                </h3>
                <div className="space-y-2">
                  {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map(day => {
                    const currentSchedule = (employee.schedule || {})[day] || '';
                    return (
                      <div key={day} className="flex items-center justify-between gap-4 border-b pb-2 last:border-b-0 last:pb-0">
                        <span className="text-xs font-bold text-gray-600 w-24">{t(day, day === 'Lundi' ? 'الإثنين' : day === 'Mardi' ? 'الثلاثاء' : day === 'Mercredi' ? 'الأربعاء' : day === 'Jeudi' ? 'الخميس' : day === 'Vendredi' ? 'الجمعة' : day === 'Samedi' ? 'السبت' : 'الأحد')}</span>
                        <input 
                          type="text" 
                          placeholder="Ex: 09:00 - 18:00"
                          value={currentSchedule}
                          onChange={e => handleUpdateSchedule(day, e.target.value)}
                          className="flex-1 max-w-[200px] px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 bg-white" 
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent History Grid */}
              {Object.keys(attendanceRecords).length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">{t('Historique des 10 dernières présences', 'سجل الحضور الأخير')}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {Object.entries(attendanceRecords)
                      .sort((a, b) => b[0].localeCompare(a[0]))
                      .slice(0, 10)
                      .map(([date, status]) => {
                        const colors = 
                          status === 'present' ? 'bg-green-50 text-green-700 border-green-200' :
                          status === 'late' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          status === 'absent' ? 'bg-red-50 text-red-700 border-red-200' :
                          'bg-blue-50 text-blue-700 border-blue-200';
                        return (
                          <div key={date} className={`p-2 border rounded-lg text-center ${colors}`}>
                            <div className="text-[10px] font-bold">{formatDateFR(date)}</div>
                            <div className="text-[11px] font-black uppercase mt-1">
                              {status === 'present' ? t('Présent', 'حاضر') :
                               status === 'late' ? t('Retard', 'متأخر') :
                               status === 'absent' ? t('Absent', 'غائب') :
                               t('Congé', 'عطلة')}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* TAB 3: DOCUMENTS */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              
              {/* Add New Document Form */}
              <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                <h3 className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-1">
                  <Upload className="w-4 h-4 text-blue-600" />
                  <span>{t('Ajouter un document (Contrat, CIN, etc.)', 'إضافة وثيقة (عقد، بطاقة وطنية، إلخ)')}</span>
                </h3>
                <form onSubmit={handleAddDocument} className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">{t('Nom du document', 'اسم الوثيقة')}</label>
                      <input 
                        type="text" 
                        required
                        placeholder={t("Ex: Contrat de travail, Permis...", "مثال: عقد العمل، رخصة السياقة...")}
                        value={docName}
                        onChange={e => setDocName(e.target.value)}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">{t('Date d\'expiration', 'تاريخ انتهاء الصلاحية')}</label>
                      <input 
                        type="date" 
                        value={docExpiry}
                        onChange={e => setDocExpiry(e.target.value)}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg outline-none bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">{t('Fichier (Image ou PDF)', 'الملف (صورة أو PDF)')}</label>
                    <input 
                      type="file" 
                      id="staff-doc-file"
                      required
                      accept="image/*,application/pdf"
                      onChange={e => setDocFile(e.target.files[0])}
                      className="w-full text-xs"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold text-xs rounded-lg transition"
                  >
                    {submitting ? t('Téléversement...', 'جاري الرفع...') : t('Ajouter le document', 'إضافة الوثيقة')}
                  </button>
                </form>
              </div>

              {/* Document List */}
              <div>
                <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">
                  {t('Documents de l\'employé', 'وثائق الموظف')} ({(employee.documents || []).length})
                </h3>
                
                {(!employee.documents || employee.documents.length === 0) ? (
                  <div className="p-6 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">{t('Aucun document sauvegardé.', 'لا توجد وثائق محفوظة بعد.')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {employee.documents.map((doc, idx) => {
                      const isExpired = doc.expiryDate && new Date(doc.expiryDate) < new Date();
                      return (
                        <div key={idx} className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-bold text-xs text-gray-900 truncate">{doc.name}</div>
                            {doc.expiryDate ? (
                              <div className={`text-[10px] font-semibold mt-1 ${isExpired ? 'text-red-650' : 'text-gray-500'}`}>
                                📅 Expiration: {formatDateFR(doc.expiryDate)}
                                {isExpired && <span className="ms-1 bg-red-100 text-red-800 text-[8px] font-black px-1 py-0.5 rounded uppercase">Expired</span>}
                              </div>
                            ) : (
                              <div className="text-[10px] text-gray-400 mt-1">♾️ permanent</div>
                            )}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => setViewDoc({ url: doc.url, title: doc.name })}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition"
                              title="Ouvrir / Visualiser"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteDocument(idx)}
                              className="p-1.5 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition"
                              title="Supprimer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          )}

          {/* TAB 4: PERMISSIONS */}
          {activeTab === 'permissions' && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-xl mb-4">
                <p className="text-xs text-blue-800 font-medium leading-relaxed">
                  💡 {t(
                    'Ces autorisations contrôlent les sections auxquelles l\'employé aura accès s\'il se connecte à l\'application avec ses identifiants.',
                    'تحدد هذه الصلاحيات الأقسام التي يمكن للموظف الدخول إليها في حالة تسجيل دخوله للتطبيق.'
                  )}
                </p>
              </div>
              
              <div className="space-y-2">
                {[
                  { key: 'dashboard', label: t('Accès au Tableau de Bord', 'عرض لوحة التحكم الرئيسية'), desc: t('Permet de voir le résumé des bénéfices, alertes et statistiques.', 'مشاهدة الأرباح، التنبيهات، والإحصائيات.') },
                  { key: 'clients', label: t('Gestion des Clients', 'إدارة الزبناء والعملاء'), desc: t('Permet d\'ajouter, modifier et archiver les clients.', 'إضافة، تعديل، وأرشفة الزبناء.') },
                  { key: 'cars', label: t('Gestion des Véhicules', 'إدارة السيارات والأسطول'), desc: t('Permet de gérer le parc automobile et les vidanges.', 'إدارة أسطول السيارات ومحاضر الصيانة.') },
                  { key: 'rentals', label: t('Gestion des Contrats de Location', 'إدارة عقود الكراء'), desc: t('Permet de créer, éditer, signer et clôturer les locations.', 'تحرير، توقيع، وإقفال عقود الكراء.') },
                  { key: 'finances', label: t('Accès aux Finances & Invoices', 'الاطلاع على المالية والفواتير'), desc: t('Permet de voir les règlements, chèques, caisse et chiffres.', 'الاطلاع على الصندوق، الشيكات، المداخيل، والفواتير.') },
                  { key: 'gps', label: t('Contrôle des commandes GPS (Coupure essence)', 'التحكم في أوامر GPS (توقيف البنزين)'), desc: t('Accès au bouton de sécurité pour bloquer/débloquer les voitures.', 'إمكانية إرسال أمر إيقاف/تشغيل محرك السيارة عن بعد.') },
                  { key: 'admin', label: t('Super Admin / Paramètres globaux', 'المشرف الكامل / الإعدادات العامة'), desc: t('Donne les droits d\'administrateur complets pour modifier l\'agence.', 'صلاحيات المشرف الكاملة للتعديل في إعدادات الوكالة.') }
                ].map(item => {
                  const isChecked = !!(employee.permissions || {})[item.key];
                  return (
                    <div 
                      key={item.key} 
                      onClick={() => handleTogglePermission(item.key)}
                      className={`p-3 border rounded-xl flex items-center justify-between gap-4 cursor-pointer transition ${
                        isChecked 
                          ? 'bg-blue-50/30 border-blue-300 shadow-sm' 
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-gray-900">{item.label}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5 leading-normal">{item.desc}</div>
                      </div>
                      
                      {/* Custom Switch Style UI */}
                      <div className={`w-10 h-6 rounded-full shrink-0 p-0.5 transition-colors ${isChecked ? 'bg-blue-600' : 'bg-gray-200'}`}>
                        <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${isChecked ? (isAr ? '-translate-x-4' : 'translate-x-4') : 'translate-x-0'}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 5: PERFORMANCE */}
          {activeTab === 'performance' && (
            <div className="space-y-6">
              
              {/* Add New Review Form */}
              <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                <h3 className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-blue-600" />
                  <span>{t('Ajouter une évaluation', 'تسجيل تقييم أداء جديد')}</span>
                </h3>
                <form onSubmit={handleAddReview} className="space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-gray-500">{t('Note de performance:', 'تقييم الأداء:')}</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(val => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setReviewRating(val)}
                          className="text-xl transition hover:scale-110"
                        >
                          {val <= reviewRating ? '⭐' : '☆'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <textarea
                      required
                      placeholder={t("Commentaire sur le travail de l'employé...", "اكتب ملاحظتك حول مردودية الموظف...")}
                      value={reviewComment}
                      onChange={e => setReviewComment(e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg outline-none bg-white"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg transition"
                  >
                    {t('Enregistrer l\'évaluation', 'تسجيل التقييم')}
                  </button>
                </form>
              </div>

              {/* Reviews List */}
              <div>
                <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">
                  {t('Historique des évaluations', 'سجل تقييمات الأداء')} ({(employee.reviews || []).length})
                </h3>
                
                {(!employee.reviews || employee.reviews.length === 0) ? (
                  <div className="p-6 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <Star className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">{t('Aucun avis enregistré.', 'لا توجد تقييمات مسجلة بعد.')}</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                    {employee.reviews.map((rev, idx) => (
                      <div key={idx} className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex gap-0.5 text-xs">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <span key={i}>{i < rev.rating ? '⭐' : '☆'}</span>
                            ))}
                          </div>
                          <span className="text-[10px] text-gray-400 font-semibold font-mono">{formatDateFR(rev.date)}</span>
                        </div>
                        <p className="text-xs text-gray-750 font-medium leading-relaxed">{rev.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}

          {/* TAB 6: NOTES */}
          {activeTab === 'notes' && (
            <div className="space-y-6">
              
              {/* Add New Note */}
              <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                <form onSubmit={handleAddNote} className="space-y-2">
                  <label className="block text-xs font-bold text-gray-500 mb-1">{t('Ajouter une note administrative', 'إضافة ملاحظة إدارية')}</label>
                  <textarea
                    required
                    placeholder={t("Commentaires internes, avertissements ou remarques...", "اكتب ملاحظاتك الداخلية...")}
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    rows="2"
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg outline-none bg-white"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg transition"
                  >
                    {t('Ajouter la note', 'إضافة الملاحظة')}
                  </button>
                </form>
              </div>

              {/* Notes Timeline */}
              <div>
                <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">
                  {t('Remarques & timeline interne', 'مخطط الملاحظات الداخلية')} ({(employee.notesList || []).length})
                </h3>
                
                {(!employee.notesList || employee.notesList.length === 0) ? (
                  <div className="p-6 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <MessageSquare className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">{t('Aucune note interne.', 'لا توجد ملاحظات داخلية.')}</p>
                  </div>
                ) : (
                  <div className="space-y-3 relative border-l border-gray-200 pl-4 ml-2 max-h-80 overflow-y-auto pr-1">
                    {employee.notesList.map((nt, idx) => (
                      <div key={idx} className="relative">
                        {/* Timeline node */}
                        <div className="absolute -left-6 top-1.5 w-3.5 h-3.5 rounded-full bg-blue-500 border-2 border-white" />
                        <div className="p-3 bg-gray-50 border border-gray-150 rounded-xl">
                          <div className="text-[10px] text-gray-400 font-bold font-mono mb-1">
                            📅 {new Date(nt.date).toLocaleString(lang === 'ar' ? 'ar-EG' : 'fr-FR')}
                          </div>
                          <p className="text-xs text-gray-700 font-medium leading-relaxed">{nt.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}

          {/* TAB: COMMISSIONS */}
          {activeTab === 'commission' && (
            <div className="space-y-5">
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl text-center">
                  <div className="text-[9px] font-bold text-indigo-700 uppercase">{t('Commission / contrat', 'العمولة/عقد')}</div>
                  <div className="text-sm font-black text-indigo-900 font-mono mt-0.5">{employee.commissionPerRental ?? 25} {settings.currency || 'MAD'}</div>
                </div>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-center">
                  <div className="text-[9px] font-bold text-gray-600 uppercase">{t('Total contrats', 'عدد العقود')}</div>
                  <div className="text-sm font-black text-gray-900 font-mono mt-0.5">{commissionStats.count}</div>
                </div>
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                  <div className="text-[9px] font-bold text-emerald-700 uppercase">{t('Total gagné', 'إجمالي العمولات')}</div>
                  <div className="text-sm font-black text-emerald-900 font-mono mt-0.5">{commissionStats.totalEarned} {settings.currency || 'MAD'}</div>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-center">
                  <div className="text-[9px] font-bold text-blue-700 uppercase">{t('Total payé', 'المدفوع')}</div>
                  <div className="text-sm font-black text-blue-900 font-mono mt-0.5">{commissionStats.totalPaid} {settings.currency || 'MAD'}</div>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-center">
                  <div className="text-[9px] font-bold text-amber-700 uppercase">{t('Solde restant', 'الرصيد المتبقي')}</div>
                  <div className="text-sm font-black text-amber-900 font-mono mt-0.5">{commissionStats.remaining} {settings.currency || 'MAD'}</div>
                </div>
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl text-center">
                  <div className="text-[9px] font-bold text-purple-700 uppercase">{t('Dernier paiement', 'آخر دفعة')}</div>
                  <div className="text-xs font-black text-purple-900 font-mono mt-1">{commissionStats.lastPaymentDate ? formatDateFR(commissionStats.lastPaymentDate) : '—'}</div>
                </div>
              </div>

              {/* Pay commission (partial allowed) */}
              <div className="p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl">
                <div className="text-xs font-bold text-emerald-900 mb-2">💸 {t('Payer une commission', 'دفع عمولة')}</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} placeholder={t('Montant', 'المبلغ')} className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-emerald-500" />
                  <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-emerald-500" />
                  <input type="text" value={payRef} onChange={e => setPayRef(e.target.value)} placeholder={t('Référence', 'مرجع')} className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-emerald-500" />
                  <input type="text" value={payNote} onChange={e => setPayNote(e.target.value)} placeholder={t('Note (option.)', 'ملاحظة')} className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <button type="button" disabled={submitting} onClick={payCommission} className="mt-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold disabled:opacity-50">
                  {t('Enregistrer le paiement', 'تسجيل الدفعة')}
                </button>
                <p className="text-[9px] text-emerald-700 mt-1">{t('Paiement partiel autorisé. Déduit automatiquement du solde restant.', 'يُسمح بالدفع الجزئي. يُخصم تلقائياً من الرصيد المتبقي.')}</p>
              </div>

              {/* Filters + export */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[140px]">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                  <input value={commSearch} onChange={e => setCommSearch(e.target.value)} placeholder={t('Rechercher…', 'بحث…')} className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <select value={commStatus} onChange={e => setCommStatus(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white">
                  <option value="all">{t('Tous', 'الكل')}</option>
                  <option value="pending">{t('En attente', 'قيد الانتظار')}</option>
                  <option value="paid">{t('Payé', 'مدفوع')}</option>
                </select>
                <select value={commSort} onChange={e => setCommSort(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white">
                  <option value="date_desc">{t('Date ↓', 'التاريخ ↓')}</option>
                  <option value="date_asc">{t('Date ↑', 'التاريخ ↑')}</option>
                  <option value="amount_desc">{t('Montant ↓', 'المبلغ ↓')}</option>
                  <option value="amount_asc">{t('Montant ↑', 'المبلغ ↑')}</option>
                </select>
                <button type="button" onClick={exportCommissionsExcel} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg text-xs font-bold hover:bg-green-100">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                </button>
                <button type="button" onClick={exportCommissionsPdf} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs font-bold hover:bg-red-100">
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              </div>

              {/* Commission history table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-gray-500">
                        <th className="text-start px-3 py-2 font-bold">{t('Date', 'التاريخ')}</th>
                        <th className="text-start px-3 py-2 font-bold">{t('Contrat', 'العقد')}</th>
                        <th className="text-start px-3 py-2 font-bold">{t('Client', 'الزبون')}</th>
                        <th className="text-start px-3 py-2 font-bold hidden sm:table-cell">{t('Véhicule', 'السيارة')}</th>
                        <th className="text-end px-3 py-2 font-bold">{t('Comm.', 'العمولة')}</th>
                        <th className="text-center px-3 py-2 font-bold">{t('Statut', 'الحالة')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {commissionRows.length === 0 ? (
                        <tr><td colSpan="6" className="px-3 py-6 text-center text-gray-400">{t('Aucune commission.', 'لا توجد عمولات.')}</td></tr>
                      ) : commissionRows.map(r => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-gray-600">{formatDateFR(r.date)}</td>
                          <td className="px-3 py-2 font-mono">{r.contractNumber}</td>
                          <td className="px-3 py-2 font-medium text-gray-800 truncate max-w-[120px]">{r.customer}</td>
                          <td className="px-3 py-2 text-gray-600 hidden sm:table-cell truncate max-w-[120px]">{r.vehicle}</td>
                          <td className="px-3 py-2 text-end font-black font-mono text-emerald-700">{Number(r.amount) || 0}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {r.status === 'paid' ? t('Payé', 'مدفوع') : t('En attente', 'قيد الانتظار')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 7: FINANCES & SALARY LOGS */}
          {activeTab === 'finances' && (
            <div className="space-y-6">
              
              {/* Financial stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-center">
                  <div className="text-[9px] font-bold text-green-700 uppercase">{t('Total salaires', 'إجمالي الرواتب')}</div>
                  <div className="text-sm font-black text-green-900 font-mono mt-0.5">{financeStats.totalSalary} {settings.currency || 'MAD'}</div>
                </div>
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-center">
                  <div className="text-[9px] font-bold text-amber-700 uppercase">{t('Avances', 'السلفات')}</div>
                  <div className="text-sm font-black text-amber-900 font-mono mt-0.5">{financeStats.totalAdvance} {settings.currency || 'MAD'}</div>
                </div>
                <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-center">
                  <div className="text-[9px] font-bold text-blue-700 uppercase">{t('Primes', 'العلاوات')}</div>
                  <div className="text-sm font-black text-blue-900 font-mono mt-0.5">{financeStats.totalBonus} {settings.currency || 'MAD'}</div>
                </div>
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-center">
                  <div className="text-[9px] font-bold text-red-700 uppercase">{t('Pénalités', 'الخصومات')}</div>
                  <div className="text-sm font-black text-red-900 font-mono mt-0.5">{financeStats.totalPenalty} {settings.currency || 'MAD'}</div>
                </div>
              </div>
              
              <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-bold text-indigo-900">💎 {t('Net versé cumulé', 'صافي المدفوعات')}</div>
                  <div className="text-[9px] text-indigo-700 opacity-80 mt-0.5">{t('(Salaires + Primes - Pénalités)', '(الرواتب + العلاوات - الخصومات)')}</div>
                </div>
                <div className="text-xl font-black text-indigo-950 font-mono">
                  {financeStats.netTotal} {settings.currency || 'MAD'}
                </div>
              </div>

              {/* Payments History List */}
              <div>
                <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">{t('Historique des versements', 'سجل الدفعات والتسويات')} ({payments.length})</h3>
                
                {payments.length === 0 ? (
                  <div className="p-6 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <DollarSign className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">{t('Aucun versement enregistré.', 'لا توجد دفعات مسجلة بعد.')}</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {payments.map(p => {
                      const isMinus = p.type === 'penalty';
                      return (
                        <div key={p.id} className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm flex items-center justify-between gap-3 text-xs">
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-bold text-gray-800">
                                {p.type === 'salary' ? '💰 ' + t('Salaire', 'راتب') :
                                 p.type === 'advance' ? '⏩ ' + t('Avance', 'سلفة') :
                                 p.type === 'bonus' ? '🎁 ' + t('Prime', 'علاوة') :
                                 '⛔ ' + t('Pénalité', 'خصم')}
                              </span>
                              <span className={`font-black font-mono text-sm ${isMinus ? 'text-red-650' : 'text-emerald-600'}`}>
                                {isMinus ? '-' : '+'}{p.amount} {settings.currency || 'MAD'}
                              </span>
                            </div>
                            <div className="text-[10px] text-gray-500 font-semibold mt-1">
                              📅 {formatDateFR(p.paymentDate)} 
                              {p.period && <span className="ms-1.5 px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 font-normal">{p.period}</span>}
                            </div>
                            {p.note && <div className="text-[10px] text-gray-450 italic mt-1">📝 {p.note}</div>}
                          </div>
                          
                          <button
                            onClick={() => handleDeletePayment(p.id)}
                            className="p-1.5 text-red-650 hover:bg-red-50 border border-red-200 rounded-lg transition"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          )}

        </div>

      </div>

      {viewDoc && (
        <DocumentViewerModal
          url={viewDoc.url}
          title={viewDoc.title}
          onClose={() => setViewDoc(null)}
        />
      )}
    </Modal>
  );
}
